import React, { useState } from 'react'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonListHeader,
  IonLabel,
  IonList,
  IonItem,
  IonFooter,
  IonButtons,
  IonButton,
  IonToggle,
  IonLoading,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CadRecordDataPoint, CadRecordDataPoints } from '../store/cadImport.types'
import { preCadImport, wipeCadData } from '../store/cadImport.actions'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useHistory } from 'react-router-dom'
import Logger from '../../../services/logger.service'
import { getUserSelector } from '../../user/selectors'
import { getCadImportSelector } from '../selectors'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { ITripInformationState } from '../../tripInformation/store/tripInformation.types'

interface ICadMultiSelectModalProps {
  cadData: CadRecordDataPoints
  showModal: boolean
  resetModal: () => void
}

const CadMultiSelectModal: React.FC<ICadMultiSelectModalProps> = (props: ICadMultiSelectModalProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { cadData, showModal, resetModal } = props
  const selectedOptions: CadRecordDataPoints = cadData
  const cadImportState = useSelector(getCadImportSelector)
  const { importedCadData, hasImportedCadData } = cadImportState
  const user = useSelector(getUserSelector)
  const { serviceId, userId } = user
  const { dispatchID }: ITripInformationState = useSelector(getTripInformationSelector)
  const [isLoading, setIsLoading] = useState(false)

  const cancel = () => {
    dismiss()
    Logger.sensitive(cadData, `CAD Import cancelled by user: ${userId} in service: ${serviceId}`)
  }

  const dismiss = () => {
    resetModal()
  }

  const wipeAndSave = () => {
    setIsLoading(true)
    dispatch(wipeCadData())
    setTimeout(() => {
      save()
      setIsLoading(false)
    }, 4000)
  }

  const save = () => {
    resetModal()
    const filteredSelectedOption = selectedOptions.filter((o) => o.performImport)
    dispatch(preCadImport(filteredSelectedOption))
    history.push(TabConstants.CHART_ROUTE)
    Logger.sensitive(cadData, `CAD Import saved by user: ${userId} in service: ${serviceId}`)
  }

  const handleSave = () => {
    if (importedCadData?.length || hasImportedCadData) {
      const isDispatchIDEqual = cadData.find((i) => i.id === 'DISPATCH_RECORD_ID')?.value === dispatchID
      const alertState = {
        header: 'Warning',
        message: 'Importing a different CAD record will overwrite the previously imported CAD data for this PCR',
        buttons: [
          { text: 'Cancel', handler: cancel },
          { text: 'Ok', role: 'destructive', handler: isDispatchIDEqual ? save : wipeAndSave },
        ],
      }
      dispatch(showAlert(alertState))
    } else {
      save()
    }
  }

  const handleToggle = (e: CustomEvent, option: CadRecordDataPoint) => {
    if (e.detail.checked) {
      const index = selectedOptions.findIndex((o) => option.id === o.id)
      if (index === -1) {
        option.performImport = true
        selectedOptions.push(option)
      }
      else {
        selectedOptions[index].performImport = true
      }
    } else {
      if (selectedOptions.filter((o) => o.id === option.id).length > 0) {
        selectedOptions.splice(
          selectedOptions.findIndex((o) => o.id === option.id),
          1
        )
      }
    }
  }

  return (
    <IonModal
      data-testid='CadModal'
      showBackdrop={showModal}
      isOpen={showModal}
      onDidDismiss={dismiss}
      className='multi-select-modal'>
      <IonHeader className='ion-no-border'>
        <IonToolbar color='light'>
          <IonListHeader color='light'>
            <IonLabel>{t('cadImport.cadImport')}</IonLabel>
          </IonListHeader>
        </IonToolbar>
      </IonHeader>
      <IonList>
        {cadData &&
          cadData.map((option: CadRecordDataPoint) => (
            <IonItem key={`Option_${option.id}`} data-skip-auto-enhance>
              <IonLabel>{option.displayLabel}</IonLabel>
              {!option.isAllowedToToggleImport && <IonLabel>*Default import</IonLabel>}
              <IonToggle
                slot='start'
                name={option.displayLabel}
                checked={option.performImport}
                onIonChange={(e) => handleToggle(e, option)}
                disabled={!option.isAllowedToToggleImport}
              />
            </IonItem>
          ))}
      </IonList>
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              data-testid='MultiSelectCancel'
              className='btn btn-secondary mr-8'
              expand='block'
              id='multiselect-modal-cancel-button'
              onClick={() => cancel()}>
              {t('common.buttons.Cancel')}
            </IonButton>
            <IonButton
              data-testid='MultiSelectSave'
              className='btn btn-primary'
              expand='block'
              onClick={() => handleSave()}
              id='multiselect-modal-save-button'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
      {isLoading && <IonLoading data-testid='Loader' isOpen animated showBackdrop spinner={'crescent'} />}
    </IonModal>
  )
}

export default CadMultiSelectModal
